import { faTrashCan, faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonType } from "components/Button/Button";
import { ModalButtons } from "components/Modal/ModalButtons";
import { useModal } from "components/Modal/context/ModalContext";
import "components/Toast.scss";
import { PackageType, getPackageTypeString } from "features/Shared/ContentData/PackageType";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatDate, formatFileSize } from "utils/formatting";
import { useContentUpload } from "../../Uploading/hooks/useContentUpload";
import { UploadingStateType, useUploadInProgress } from "../../Uploading/hooks/useUploadInProgress";
import { useEditMode } from "../../hooks/useEditMode";
import { EDIT_FIELD, EDIT_SCOPE, MODULE_DESCRIPTION_ROWS } from "../../utils/constants";
import {
    handleImageFileChange,
    handleMultilineInputKeyDown,
    handleSingleLineInputKeyDown,
    hasBeenEdited,
    openFilePicker
} from "../../utils/uiInteractionHelpers";
import ClickBoundary from "../ClickBoundary/ClickBoundary";
import EditButtonGroup from "../EditButton/EditButtonGroup";
import SortOrder from "../SortOrder/SortOrder";
import "./SubmoduleEditor.scss";

const SubmoduleEditor = ({
	canEdit,
	moduleId,
	deviceTraining,
	submodule, // published
	currentDraft,
	index,
	total,
	onMove,
	onMoveTop,
	onMoveBottom,
	onChange,
	onUpdateSubmoduleImageFile
}) => {
	const { setEditField, isEditing, cancelEditMode } = useEditMode();
	const { openModal } = useModal();
	
	const [latestDraft, setLatestDraft] = useState({
		Name: "",
		Description: "",
		Keywords: "",
		Duration: "",
		Content: {
			StoredName: "",
			UploadDate: "",
			FileSize: 0,
			PackageType: PackageType.Invalid,
			LaunchPage: "",
			JobId: ""
		},
		ImageUrl: "",
		ImageFile: null,
		HasProgress: true
	});
	const [showContentDetails, setShowContentDetails] = useState(false);
	const { uploadZipFile, cancelUpload, uploadFilename, uploadProgressValue, clearProgress } = useContentUpload();
	const { uploadingState, setUploadingState, processComplete, setProcessComplete } = useUploadInProgress();
	const contentFileInputRef = useRef(null);
	const contentFileDataRef = useRef(null);
	const deviceFileInputRef = useRef(null);
	const toast = useRef(null);

	const initialiseSubmoduleDraftData = useCallback((draft, submodule) => {
		setLatestDraft({
			Name: draft?.[EDIT_FIELD.NAME] ?? submodule.Name ?? "",
			Description: draft?.[EDIT_FIELD.DESCRIPTION] ?? submodule.Description ?? "",
			Keywords: draft?.Keywords ?? submodule.Keywords ?? "",
			Duration: draft?.Duration ?? submodule.Duration ?? "",
			Content: {
				StoredName: draft?.StoredName ?? submodule.StoredName ?? "",
				OriginalName: draft?.OriginalName ?? submodule.OriginalName ?? "",
				UploadDate: draft?.UploadDate ?? submodule.UploadDate ?? "",
				FileSize: draft?.FileSize ?? submodule.FileSize ?? 0,
				PackageType: draft?.PackageType ?? submodule.PackageType ?? PackageType.Invalid,
				LaunchPage: draft?.LaunchPage ?? submodule.LaunchPage ?? "",
				JobId: draft?.JobId ?? ""
			},
			ImageUrl: draft?.ImageUrl || submodule.ImageUrl || "",
			HasProgress: draft?.HasProgress !== undefined ? Boolean(draft.HasProgress) : Boolean(submodule?.HasProgress)
		});
	}, []);

	// Hook to initialise input field information for editing.
	useEffect(() => {
		if (currentDraft || submodule) {
			initialiseSubmoduleDraftData(currentDraft, submodule);
		}
	}, [initialiseSubmoduleDraftData, currentDraft, submodule]);
	
	/**
	 * Required fields within SubmoduleDataDto
	 * Id (int)
	 * Name (string)
	 * Description (string)
	 * Position (int)
	 * Keywords (string?)
	 * HasProgress (bool)
	 * ImageUrl (string?)
	 * StoredName (string)
	 * OriginalName (string)
	 * UploadDate (string)
	 * FileSize (int)
	 * PackageType (int - PackageType enum)
	 * LaunchPage (string?)
	 * JobId (string?)
	 */
	const getSubmoduleDtoDraft = (overrides = {}) => ({
		...submodule,
		[EDIT_FIELD.NAME]: latestDraft.Name,
		[EDIT_FIELD.DESCRIPTION]: latestDraft.Description,
		Keywords: latestDraft.Keywords,
		Duration: latestDraft.Duration,
		[EDIT_FIELD.IMAGE_URL]: latestDraft.ImageUrl,
		HasProgress: latestDraft.HasProgress,
		StoredName: latestDraft.Content.StoredName,
		OriginalName: latestDraft.Content.OriginalName,
		UploadDate: latestDraft.Content.UploadDate,
		FileSize: latestDraft.Content.FileSize,
		PackageType: latestDraft.Content.PackageType,
		LaunchPage: latestDraft.Content.LaunchPage,
		JobId: latestDraft.Content.JobId,
		...overrides
	});

	const handleTextUpdate = (override) => {
		const draftUpdate = {
			...latestDraft,
			...override
		}
		setLatestDraft(draftUpdate);
		onChange(getSubmoduleDtoDraft(override));
	}

	const handleHasProgress = () => {
		const newValue = !latestDraft.HasProgress;
		const draftUpdate = {
			...latestDraft,
			HasProgress: newValue
		}
		setLatestDraft(draftUpdate);
		onChange(getSubmoduleDtoDraft({ HasProgress: newValue }));
	};

	const onZipFileUploadSuccess = () => {
		setUploadingState(UploadingStateType.Validating);
	};

	const onZipFileProcessSuccess = (data, jobId) => {
		setUploadingState(UploadingStateType.Processing);

		const fileSize = contentFileDataRef.current.size;
		contentFileDataRef.current = null;

		const validateData = data.packageValidation;
		const draftUpdate = {
			...latestDraft,
			Content: {
				StoredName: data.proposedContainer,
				OriginalName: uploadFilename,
				PackageType: validateData.Type,
				FileSize: fileSize,
				UploadDate: validateData.UploadDate,
				LaunchPage: validateData.LaunchPage,
				JobId: jobId
			}
		}
		setLatestDraft(draftUpdate);
		onChange(getSubmoduleDtoDraft({
			StoredName: data.proposedContainer,
			OriginalName: uploadFilename,
			PackageType: validateData.Type,
			FileSize: fileSize,
			UploadDate: validateData.UploadDate,
			LaunchPage: validateData.LaunchPage,
			JobId: jobId
		}));
	};

	const onZipFileError = (error) => {
		contentFileDataRef.current = null;
		toast.current.show({
			severity: "error",
			summary: "Cannot upload Content!",
			detail: error,
			sticky: true
		});
		setUploadingState(UploadingStateType.None);
	}

	const handleConfirmUploadLargeZipFile = (yes) => {
		if (yes) {
			setUploadingState(UploadingStateType.Uploading);
			const existingDraft = (latestDraft.Content.StoredName && latestDraft.Content.JobId) ? latestDraft.Content.StoredName : null;
			uploadZipFile(existingDraft, contentFileDataRef.current, onZipFileUploadSuccess, onZipFileProcessSuccess, onZipFileError);
		}
		else {
			contentFileDataRef.current = null;
		}
	};

	const handleFileChange = async (e) => {
		if (contentFileDataRef.current) {
			return;
		}

		contentFileDataRef.current = e.target.files && e.target.files[0];
		if (!contentFileDataRef.current) {
			return;
		}
		if (contentFileDataRef.current.size > 500 * 1024 * 1024) {
			openModal(
				null,
				<div>
					This file is over 500Mb in size.
					<br/>
					If you have not done so already, you may be able to compress the videos to bring the total file size below our limit.
				</div>,
				ModalButtons.Ok,
				() => {
					clearProgress();
					contentFileDataRef.current = null;
				}
			);
			return;
		}
		if (contentFileDataRef.current.size > 150 * 1024 * 1024) {
			openModal(
				null,
				<div>
					This file is over 150Mb in size.If you have not done so already, you may need to compress the videos.
					<br />
					Are you sure you want to continue?
				</div>,
				ModalButtons.YesNo,
				handleConfirmUploadLargeZipFile
			);
			return;
		}
		setUploadingState(UploadingStateType.Uploading);
		const existingDraft = (latestDraft.Content.StoredName && latestDraft.Content.JobId) ? latestDraft.Content.StoredName : null;
		uploadZipFile(existingDraft, contentFileDataRef.current, onZipFileUploadSuccess, onZipFileProcessSuccess, onZipFileError);
	};

	const handleCancelUpload = () => {
		cancelUpload();
	};

	useEffect(() => {
		if (processComplete && uploadingState !== UploadingStateType.None) {
			toast.current.show({
				severity: processComplete.success ? "success" : "error",
				summary: processComplete.success ? "Processing complete" : "Cannot process Content!",
				detail: processComplete.error || "Content processed successfully!",
				sticky: processComplete.error
			});
			setProcessComplete(null);
			setUploadingState(UploadingStateType.None);
		}
	}, [processComplete, setProcessComplete, uploadingState, setUploadingState]);

	const handleCommitImageFile = (fileData) => {
		const draftUpdate = {
			...latestDraft,
			ImageFile: fileData
		}
		setLatestDraft(draftUpdate);
		onUpdateSubmoduleImageFile({
			id: submodule.Id,
			data: fileData
		});
		onChange(getSubmoduleDtoDraft());
	};
	
	const fieldGetters = {
		[EDIT_FIELD.NAME]: () => latestDraft.Name,
		[EDIT_FIELD.DESCRIPTION]: () => latestDraft.Description,
		Keywords: () => latestDraft.Keywords,
		Duration: () => latestDraft.Duration,
		[EDIT_FIELD.IMAGE_URL]: () => latestDraft.ImageUrl
	};

	const setSubmoduleEditField = (field) => {
		setEditField({
			scope: EDIT_SCOPE.SUBMODULE,
			id: submodule.Id,
			parentId: moduleId,
			field: field
		});
	};

	return (
		<div className="editable-submodule">
			<div className="editable-submodule-sidebar">
				<SortOrder
					index={index}
					total={total}
					onMove={onMove}
					onMoveTop={onMoveTop}
					onMoveBottom={onMoveBottom}
					label="Submodule Position"
				/>
				<div className="editable-module-sidebar-delete">
					<FontAwesomeIcon icon={faTrashCan} />
					<p>
						Delete
						<br />
						Submodule
					</p>
				</div>
			</div>

			{/* Submodule Name */}
			<div className="editable-submodule-main">
				<div className="editable-submodule-header">
					<ClickBoundary>
						{isEditing(EDIT_SCOPE.SUBMODULE, EDIT_FIELD.NAME, submodule.Id, moduleId) ? (
							<InputText
								autoFocus
								maxLength={150}
								value={latestDraft.Name}
								onChange={(e) => setLatestDraft((d) => ({ ...d, [EDIT_FIELD.NAME]: e.target.value }))}
								onKeyDown={(e) => handleSingleLineInputKeyDown(e, cancelEditMode)}
								onBlur={() => handleTextUpdate({ [EDIT_FIELD.NAME]: latestDraft.Name })}
								className="title-textbox"
							/>
						) : (
							<div className="editable-submodule-title">
								<h2>{latestDraft.Name || "Add Name"}</h2>
								<EditButtonGroup
									show={canEdit}
									onEdit={() => setSubmoduleEditField(EDIT_FIELD.NAME)}
									onUndo={() => handleTextUpdate({ [EDIT_FIELD.NAME]: submodule.Name })}
									showUndo={hasBeenEdited(submodule, fieldGetters, EDIT_FIELD.NAME)}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Submodule Description */}
				<div className="editable-submodule-row">
					<ClickBoundary>
						{isEditing(EDIT_SCOPE.SUBMODULE, EDIT_FIELD.DESCRIPTION, submodule.Id, moduleId) ? (
							<InputTextarea
								maxLength={3000}
								value={latestDraft.Description}
								onChange={(e) => setLatestDraft((d) => ({ ...d, [EDIT_FIELD.DESCRIPTION]: e.target.value }))}
								onKeyDown={(e) => handleMultilineInputKeyDown(e, cancelEditMode)}
								onBlur={() => handleTextUpdate({ [EDIT_FIELD.DESCRIPTION]: latestDraft.Description })}
								rows={MODULE_DESCRIPTION_ROWS}
								autoResize
								className="description-textbox"
							/>
						) : (
							<div className="editable-submodule-main-description">
								<p>{latestDraft.Description || "Add Description"}</p>
								<EditButtonGroup
									show={canEdit}
									onEdit={() => setSubmoduleEditField(EDIT_FIELD.DESCRIPTION)}
									onUndo={() => handleTextUpdate({ [EDIT_FIELD.DESCRIPTION]: submodule.Description })}
									showUndo={hasBeenEdited(submodule, fieldGetters, EDIT_FIELD.DESCRIPTION)}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Keywords */}
				<div className="editable-submodule-row">
					<h4>Keywords</h4>
					<ClickBoundary>
						{isEditing(EDIT_SCOPE.SUBMODULE, "Keywords", submodule.Id, moduleId) ? (
							<InputTextarea
								maxLength={3000}
								value={latestDraft.Keywords}
								onChange={(e) => setLatestDraft((d) => ({ ...d, Keywords: e.target.value }))}
								onKeyDown={(e) => handleMultilineInputKeyDown(e, cancelEditMode)}
								onBlur={() => handleTextUpdate({ Keywords: latestDraft.Keywords })}
								rows={MODULE_DESCRIPTION_ROWS}
								autoResize
								className="field-textbox"
								placeholder="e.g. Adding, Users, Admin"
							/>
						) : (
							<div className="field-value">
								<p>{latestDraft.Keywords || "Add keywords"}</p>
								<EditButtonGroup
									show={canEdit}
									onEdit={() => setSubmoduleEditField("Keywords")}
									onUndo={() => handleTextUpdate({ Keywords: submodule.Keywords })}
									showUndo={hasBeenEdited(submodule, fieldGetters, "Keywords")}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Duration (Estimated Runtime) */}
				<div className="editable-submodule-row">
					<h4>Estimated Runtime</h4>
					<ClickBoundary>
						{isEditing(EDIT_SCOPE.SUBMODULE, "Duration", submodule.Id, moduleId) ? (
							<InputText
								maxLength={20}
								value={latestDraft.Duration}
								onChange={(e) => setLatestDraft((d) => ({ ...d, Duration: e.target.value }))}
								onKeyDown={(e) => handleSingleLineInputKeyDown(e, cancelEditMode)}
								onBlur={() => handleTextUpdate({ Duration: latestDraft.Duration })}
								className="field-textbox"
								placeholder="minutes"
							/>
						) : (
							<div className="field-value">
								<p>
									{latestDraft.Duration != null && latestDraft.Duration !== "" ? `${latestDraft.Duration} min` : "Not set"}
								</p>
								<EditButtonGroup
									show={canEdit}
									onEdit={() => setSubmoduleEditField("Duration")}
									onUndo={() => handleTextUpdate({ Duration: submodule.Duration })}
									showUndo={hasBeenEdited(submodule, fieldGetters, "Duration")}
								/>
							</div>
						)}
					</ClickBoundary>
				</div>

				{/* Show Progress */}
				<div className="editable-submodule-row">
					<h4>Show Progress</h4>
					<div className="field-value">
						<label className="checkbox-inline">
							<input
								type="checkbox"
								checked={latestDraft.HasProgress}
								onChange={handleHasProgress}
								className="editable-submodule-row-checkbox"
							/>
						</label>
					</div>
				</div>

				{/* Content */}
				<div className="editable-submodule-row">
					<h4>Content</h4>
					<ClickBoundary>
						<div className="content-upload">
							<p>{latestDraft.Content.StoredName || "None"}</p>
							<div className="content-upload-buttons">
								<div
									className={`content-upload-buttons-upload${uploadingState !== UploadingStateType.None ? " disabled" : ""}`}
									onClick={() => openFilePicker(contentFileInputRef)}
								>
									<p>Upload +</p>
									<input
										type="file"
										accept="application/x-zip-compressed"
										ref={contentFileInputRef}
										style={{ display: "none" }}
										onChange={handleFileChange}
									/>
								</div>
								{latestDraft.Content.StoredName && (
									<div className="content-upload-buttons-details" onClick={() => setShowContentDetails((prev) => !prev)}>
										<p>{showContentDetails ? "Hide Details" : "Show Details"}</p>
									</div>
								)}
							</div>

							{/* Details */}
							{showContentDetails && (
								<div className="content-upload-details">
									<div className="content-upload-details-detail">
										<h4>Package Type</h4>
										<p>{getPackageTypeString(latestDraft.Content.PackageType)}</p>
									</div>
									<div className="content-upload-details-detail">
										<h4>Upload Date</h4>
										<p>{formatDate(latestDraft.Content.UploadDate, true)}</p>
									</div>
									<div className="content-upload-details-detail">
										<h4>File Size</h4>
										<p>{formatFileSize(latestDraft.Content.FileSize)}</p>
									</div>
								</div>
							)}
						</div>
					</ClickBoundary>
				</div>

				{/* Device Image */}
				{deviceTraining && (
					<div className="editable-submodule-row">
						<h4>Device Image</h4>
						<div className="editable-submodule-row-device">
							<div className="editable-submodule-row-device-image">
								{latestDraft.ImageUrl.length > 0
									? <img src={latestDraft.ImageUrl} alt={latestDraft.Name} className="editable-submodule-row-device-image-img" />
									: <p>Add Device Image</p>
								}
							</div>
							<div className="editable-submodule-row-device-image-upload">
								<input
									ref={deviceFileInputRef}
									type="file"
									accept="image/*"
									style={{ display: "none" }}
									onChange={(e) => handleImageFileChange(e, deviceFileInputRef, openModal, handleCommitImageFile)}
								/>
								<EditButtonGroup
									static
									editText="Edit Image"
									show={canEdit}
									onEdit={() => openFilePicker(deviceFileInputRef)}
									onUndo={() => handleTextUpdate({ [EDIT_FIELD.IMAGE_URL]: submodule.ImageUrl })}
									showUndo={hasBeenEdited(submodule, fieldGetters, EDIT_FIELD.IMAGE_URL)}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
			{/* Upload Progress */}
			{uploadingState !== UploadingStateType.None && (
				<div className="upload-submodule-content">
					<h4>{uploadingState}...</h4>
					<p>{uploadFilename}</p>
					<ProgressBar
						className="upload-submodule-progressbar"
						mode={uploadingState === UploadingStateType.Uploading ? "determinate" : "indeterminate"}
						value={uploadProgressValue}
					/>
					{uploadingState === UploadingStateType.Uploading && (
						<Button type={ButtonType.Tertiary} text="Cancel" onClick={handleCancelUpload} icon={faX} flipped />
					)}
				</div>
			)}
			<Toast ref={toast} />
		</div>
	);
};

export default SubmoduleEditor;